import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Keyboard, Platform, StyleSheet, TextInput, TouchableHighlight, View } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';
import { useNodeHandle } from '@hooks';

import { WebKeyboard } from './WebKeyboard/WebKeyboard';

import { isAndroid, isWeb } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';

export const TWCTextInput = forwardRef(
  (
    {
      accessibilityHint,
      accessibilityLabel,
      borderColorDefault,
      testID,
      style,
      styleFocused,
      placeholderTextColor,
      onBlur,
      onFocus,
      returnKeyType,
      nextFocusUp,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      onSubmitEditing,
      onChangeText,
      useMiniKeyboard,
      offsetX,
      keyboardWidth,
      keyboardBackground,
      hideKeyboardInput,
      keyboardDismissConfig,
      keyboardWrapConfig,
      clearOnBeginEditing,
      onInputHandle,
      value,
      defaultValue,
      ...properties
    },
    reference,
  ) => {
    const textInputReference = useRef(null);
    const textInputDefaultStyle = [
      styles.textInput,
      borderColorDefault ? borderColorDefault : styles.textInputBorder,
      style,
    ];
    const [textInputStyle, setTextInputStyle] = useState(textInputDefaultStyle);
    const [keyboardVisible, setKeyboardVisible] = useState(false);

    const inputHandle = useNodeHandle(textInputReference);

    useEffect(() => {
      if (isWeb && textInputReference.current) {
        textInputReference.current.scrollLeft = textInputReference.current.scrollWidth;
      }
    }, [value]);

    useEffect(() => {
      if (inputHandle && onInputHandle) {
        onInputHandle(inputHandle);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputHandle]);

    return (
      <>
        <TouchableHighlight
          accessibilityHint={accessibilityHint}
          accessibilityLabel={accessibilityLabel}
          accessibilityLiveRegion={'assertive'}
          activeOpacity={1}
          nativeID={testID}
          nextFocusDown={nextFocusDown}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          nextFocusUp={nextFocusUp}
          ref={reference}
          testID={testID}
          underlayColor={'clear'}
          onBlur={event => {
            setTextInputStyle(textInputDefaultStyle);
            if (onBlur) {
              onBlur(event);
            }
          }}
          onFocus={event => {
            setTextInputStyle([styles.textInput, styles.textInputFocused, styleFocused]);
            if (onFocus) {
              onFocus(event);
              if (isAndroid) {
                Keyboard.dismiss();
              }
            }
          }}
          onPress={event => {
            const eventType = event.eventType;
            const tvosPress = Platform.isTVOS && eventType === 'select';
            // Raw press event (no event type)- only fired once
            // Other press events are for keydown and keyup, may or may not be fired depending on platform

            if (!eventType || tvosPress) {
              if (clearOnBeginEditing) {
                onChangeText('');
              }

              if (isWeb) {
                setKeyboardVisible(true);
              } else {
                textInputReference.current.focus();
              }
            }
          }}
        >
          <View {...properties}>
            <TextInput
              nativeID={`${testID}_input`}
              placeholderTextColor={placeholderTextColor ? placeholderTextColor : '#BFDBEA'}
              ref={textInputReference}
              returnKeyLabel={returnKeyType ? returnKeyType : 'done'}
              returnKeyType={returnKeyType ? returnKeyType : 'done'}
              style={[textInputStyle, style]}
              testID={`${testID}_input`}
              value={value ?? ''}
              onBlur={() => {
                FOCUS_NOW(reference);
              }}
              onChangeText={text => {
                const dismiss = onChangeText(text);

                if (dismiss === true) {
                  FOCUS_NOW(reference);
                  Keyboard.dismiss();
                  onSubmitEditing && onSubmitEditing();
                }
              }}
              onSubmitEditing={onSubmitEditing}
              {...properties}
            />
          </View>
        </TouchableHighlight>
        {isWeb && keyboardVisible ? (
          <WebKeyboard
            backgroundColor={keyboardBackground}
            defaultValue={value ?? defaultValue}
            dismissOnLeft={keyboardDismissConfig?.left ?? false}
            dismissOnRight={keyboardDismissConfig?.right ?? false}
            dismissOnUp={keyboardDismissConfig?.up ?? false}
            hideKeyboardInput={hideKeyboardInput}
            keyboardWidth={keyboardWidth}
            numericOnly={properties?.numericOnly ?? false}
            offsetX={offsetX}
            returnKeyLabel={returnKeyType ? returnKeyType : 'done'}
            useMiniKeyboard={useMiniKeyboard}
            wrapHorizontal={keyboardWrapConfig?.horizontal ?? true}
            wrapVertical={keyboardWrapConfig?.vertical ?? true}
            onSubmit={() => {
              setKeyboardVisible(false);
              FOCUS_NOW(reference);
              onSubmitEditing && onSubmitEditing();
            }}
            onTextChanged={text => {
              const shouldDismiss = onChangeText(text);

              if (shouldDismiss === true) {
                setKeyboardVisible(false);
                FOCUS_NOW(reference);
                onSubmitEditing && onSubmitEditing();
              }
            }}
          />
        ) : null}
      </>
    );
  },
);

TWCTextInput.propTypes = {
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  placeholderTextColor: PropTypes.string,
  styleFocused: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onKeyPress: PropTypes.func,
  returnKeyType: PropTypes.string,
  accessibilityHint: PropTypes.string,
  accessibilityLabel: PropTypes.string,
  borderColorDefault: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
  testID: PropTypes.string,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.number]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onSubmitEditing: PropTypes.func,
  onChangeText: PropTypes.func,
  useMiniKeyboard: PropTypes.bool,
  offsetX: PropTypes.number,
  keyboardWidth: PropTypes.number,
  keyboardBackground: PropTypes.string,
  hideKeyboardInput: PropTypes.bool,
  keyboardDismissConfig: PropTypes.object,
  keyboardWrapConfig: PropTypes.object,
  clearOnBeginEditing: PropTypes.bool,
  onInputHandle: PropTypes.func,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
};

TWCTextInput.defaultProps = {
  onFocus: () => {},
  onBlur: () => {},
  placeholderTextColor: null,
  styleFocused: null,
  onKeyPress: () => {},
  returnKeyType: null,
  accessibilityHint: null,
  accessibilityLabel: null,
  borderColorDefault: null,
  testID: null,
  style: null,
  nextFocusUp: null,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  onSubmitEditing: () => {},
  onChangeText: () => {},
  useMiniKeyboard: false,
  offsetX: null,
  keyboardWidth: 1920,
  keyboardBackground: null,
  hideKeyboardInput: false,
  keyboardDismissConfig: null,
  keyboardWrapConfig: null,
  clearOnBeginEditing: false,
  onInputHandle: () => {},
  value: null,
  defaultValue: null,
};

const styles = StyleSheet.create({
  textInput: {
    backgroundColor: Theme.curiousBlue,
    borderRadius: Theme.tileBorderRadius,
    borderWidth: Theme.px(4),
    color: Theme.lightBlue,
    fontFamily: 'Roboto-Light',
    fontSize: Theme.px(40),
    height: Theme.px(93),
    padding: Theme.px(23),
    width: Theme.px(800),
  },
  textInputBorder: {
    borderColor: Theme.seagullBlue,
  },
  textInputFocused: {
    backgroundColor: Theme.white,
    borderColor: Theme.white,
    color: Theme.colorFocusText,
  },
});
